import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { titleCase } from "@/lib/format";
import { toast } from "sonner";
import { PushToggle } from "@/components/PushToggle";
import { fmtStudy } from "@/lib/tcfLevel";
import {
  Mic, BookMarked, PenLine, MessageSquareQuote, ArrowRight, CalendarDays, Target,
  Crown, Clock, PenSquare, Sparkles, Send, ClipboardCheck, Rocket, LineChart, BookOpenCheck, Lock,
  Ticket, MapPin, Loader2, Headphones, CheckCircle2, TrendingUp, Award,
} from "lucide-react";

const AGENT_ICONS = { speaking_coach: Mic, vocabulary_coach: BookMarked, sentence_builder: PenLine, point_of_view: MessageSquareQuote };
const CLB_MAP = { B1: 5, B2: 7, C1: 9, C2: 10 };

function trialCountdown(iso) {
  if (!iso) return null;
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "0h 0m";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
}

// Normalise legacy grouped levels (e.g. "B1/B2") to a single level for display.
function normLevel(lvl) {
  if (!lvl) return "B1";
  if (CLB_MAP[lvl]) return lvl;
  if (lvl.includes("B1")) return "B1";
  if (lvl.includes("B2")) return "B2";
  if (lvl.includes("C2")) return "C2";
  if (lvl.includes("C1")) return "C1";
  return "B1";
}

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const [agents, setAgents] = useState([]);
  const [access, setAccess] = useState(null);
  const [topics, setTopics] = useState({ writing: [], speaking: [] });
  const [examDate, setExamDate] = useState(user?.exam_date || "");
  const [savingDate, setSavingDate] = useState(false);
  const [savingLevel, setSavingLevel] = useState(false);
  const [subType, setSubType] = useState("writing");
  const [subText, setSubText] = useState("");
  const [subLoc, setSubLoc] = useState("");
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [examProgress, setExamProgress] = useState(null);
  const [dashSkill, setDashSkill] = useState("reading");

  useEffect(() => {
    api.get("/agents").then((r) => { setAgents(r.data.agents); setAccess(r.data.access); }).catch((e) => console.error("Failed to load agents", e));
    api.get("/topics/latest").then((r) => setTopics(r.data)).catch((e) => console.error("Failed to load topics", e));
  }, []);

  useEffect(() => {
    if (!user?.is_premium) { setExamProgress(null); return; }
    api.get("/exams/progress").then((r) => setExamProgress(r.data)).catch(() => setExamProgress(null));
  }, [user?.is_premium]);

  const saveExamDate = async () => {
    if (!examDate) return;
    setSavingDate(true);
    try { await api.patch("/profile/exam-date", { exam_date: examDate }); await refreshUser(); toast.success("Exam date saved!"); }
    catch { toast.error("Could not save."); }
    finally { setSavingDate(false); }
  };

  const changeLevel = async (lvl) => {
    if (savingLevel || lvl === normLevel(user?.target_level)) return;
    setSavingLevel(true);
    try { await api.patch("/profile/target-level", { target_level: lvl }); await refreshUser(); toast.success(`Target set to ${lvl} (CLB ${CLB_MAP[lvl]}).`); }
    catch { toast.error("Could not update target."); }
    finally { setSavingLevel(false); }
  };

  const submitTopic = async () => {
    if (!subText.trim()) return;
    try { await api.post("/topics/submit", { type: subType, content: subText, original_language: "auto", exam_location: subLoc }); setSubText(""); setSubLoc(""); toast.success("Thanks! Your topic was submitted for review."); }
    catch { toast.error("Could not submit."); }
  };

  const redeemPromo = async () => {
    if (!redeemCode.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/promo/redeem", { code: redeemCode });
      await refreshUser();
      toast.success(data.message || "Premium activated! ✦");
      setRedeemCode("");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid promo code."); }
    finally { setRedeeming(false); }
  };

  const isPremium = user?.is_premium;
  const trialActive = access?.trial_active;
  const target = normLevel(user?.target_level);
  const daysToExam = user?.exam_date ? Math.ceil((new Date(user.exam_date) - new Date()) / 86400000) : null;

  return (
    <div data-testid="dashboard-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <p className="text-muted-foreground">{t("dash.hello")}</p>
        <h1 className="font-display text-4xl font-semibold">{titleCase(user?.name)} 🦉</h1>
      </motion.div>

      {/* Access banner */}
      <div className={`mt-6 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4 ${isPremium ? "emerald-surface text-white" : "bg-card border border-border"}`} data-testid="access-banner">
        <div className="flex items-center gap-3">
          <div className={`h-11 w-11 rounded-xl grid place-items-center ${isPremium ? "bg-white/15" : "emerald-surface"}`}>
            {isPremium ? <Crown className="h-5 w-5 text-white" /> : <Sparkles className="h-5 w-5 text-white" />}
          </div>
          <div>
            <p className="font-semibold">{isPremium ? t("dash.premiumActive") : trialActive ? t("dash.trialActive") : access?.trial_started ? t("dash.trialEnded") : t("dash.trialStart")}</p>
            <p className={`text-sm ${isPremium ? "text-white/80" : "text-muted-foreground"}`}>
              {isPremium ? t("dash.premiumDesc") :
               trialActive ? `${trialCountdown(access?.trial_ends_at)} left.` :
               access?.trial_started ? t("dash.trialEndedDesc") : t("dash.trialStartDesc")}
            </p>
          </div>
        </div>
        {!isPremium && (
          <Link to="/premium" data-testid="banner-premium-btn" className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity">
            {trialActive ? t("dash.seePremium") : t("dash.goPremium")}
          </Link>
        )}
      </div>

      {/* Top cards */}
      <div className="grid md:grid-cols-2 gap-5 mt-6">
        <div className="rounded-2xl bg-card border border-border p-6" data-testid="target-level-card">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><Target className="h-4 w-4 text-primary" /> {t("dash.yourTarget")}</div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {["B1", "B2"].map((l) => (
              <button key={l} onClick={() => changeLevel(l)} disabled={savingLevel} data-testid={`target-level-${l}`}
                className={`rounded-xl py-4 text-center transition-colors disabled:opacity-60 ${target === l ? "emerald-surface text-white" : "bg-secondary text-muted-foreground hover:bg-accent"}`}>
                <div className="font-display text-xl font-semibold">{l}</div>
                <div className="text-[11px] opacity-80">CLB {CLB_MAP[l]}</div>
              </button>
            ))}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {["C1", "C2"].map((l) => (
              <div key={l} data-testid={`target-level-${l}-soon`} className="rounded-xl py-2 text-center bg-secondary/40 text-muted-foreground cursor-not-allowed">
                <div className="inline-flex items-center gap-1 text-sm font-semibold"><Lock className="h-3 w-3" /> {l}</div>
                <div className="text-[10px] opacity-70">Coming soon</div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-muted-foreground">{t("dash.targetHelp")}</p>
        </div>

        <div className="rounded-2xl bg-card border border-border p-6" data-testid="exam-date-card">
          <div className="flex items-center gap-2 text-muted-foreground text-sm"><CalendarDays className="h-4 w-4 text-primary" /> {t("dash.examDate")}</div>
          {user?.exam_date && daysToExam >= 0 ? (
            <div className="mt-3">
              <div className="font-display text-3xl font-semibold text-primary">{daysToExam} {t("dash.examDays")}</div>
              <p className="text-sm text-muted-foreground">{t("dash.examUntil")} {new Date(user.exam_date).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</p>
            </div>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">{t("dash.examEmpty")}</p>
          )}
          <div className="mt-4 flex gap-2">
            <input type="date" data-testid="exam-date-input" value={examDate ? examDate.slice(0, 10) : ""} onChange={(e) => setExamDate(e.target.value)}
              className="flex-1 h-11 px-3 rounded-xl bg-background border border-border focus:outline-none focus:ring-2 focus:ring-ring" />
            <button onClick={saveExamDate} disabled={savingDate || !examDate} data-testid="save-exam-date-btn"
              className="px-4 rounded-xl bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50">{t("common.save")}</button>
          </div>
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-3" data-testid="reminders-row">
            <div>
              <p className="text-sm font-semibold">{t("dash.reminders")}</p>
              <p className="text-xs text-muted-foreground">{t("dash.remindersDesc")}</p>
            </div>
            <PushToggle />
          </div>
        </div>
      </div>

      {/* TCF Mocks progress summary */}
      {isPremium && examProgress && (
        <div className="mt-10" data-testid="dash-progress">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><LineChart className="h-6 w-6 text-primary" /> Your TCF Mocks progress</h2>
            <div className="flex items-center gap-2">
              <div className="inline-flex rounded-full bg-secondary p-1" data-testid="dash-progress-skill-tabs">
                {[["reading", "Reading", BookOpenCheck], ["listening", "Listening", Headphones]].map(([key, label, Icon]) => (
                  <button key={key} onClick={() => setDashSkill(key)} data-testid={`dash-skill-${key}`}
                    className={`inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-sm font-semibold transition-colors ${dashSkill === key ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>
              <Link to={`/exams?skill=${dashSkill}`} data-testid="dash-progress-all" className="text-sm font-semibold text-primary hover:underline">See all tests</Link>
            </div>
          </div>
          {(() => {
            const p = examProgress[dashSkill];
            if (!p) return null;
            const donePct = p.total_tests ? Math.round((p.completed / p.total_tests) * 100) : 0;
            const stats = [
              { icon: CheckCircle2, label: "Quizzes done", value: `${p.completed}/${p.total_tests}`, sub: `${donePct}% complete`, accent: "text-primary", testid: "dash-stat-completed" },
              { icon: Clock, label: "Study time", value: fmtStudy(p.study_seconds), sub: "across all attempts", accent: "text-orange-500", testid: "dash-stat-study" },
              { icon: TrendingUp, label: "Attempts", value: p.total_attempts, sub: "total submissions", accent: "text-purple-600", testid: "dash-stat-attempts" },
              { icon: Target, label: "Success rate", value: `${p.avg_pct}%`, sub: "avg. accuracy", accent: "text-green-600", testid: "dash-stat-success" },
              { icon: Award, label: "TCF level", value: p.overall ? p.overall.label : "—", sub: p.overall ? `${p.overall.score_699}/699 avg.` : "no attempts yet", accent: "text-emerald-700", testid: "dash-stat-level" },
            ];
            return (
              <div className="mt-4 grid grid-cols-2 lg:grid-cols-5 gap-4">
                {stats.map((s) => (
                  <div key={s.testid} data-testid={s.testid} className="rounded-2xl bg-card border border-border p-4 sm:p-5">
                    <div className="flex items-center justify-between text-muted-foreground text-xs sm:text-sm font-medium"><span>{s.label}</span><s.icon className="h-4 w-4 text-primary" /></div>
                    <div className={`mt-2 font-display text-2xl sm:text-3xl font-semibold ${s.accent}`}>{s.value}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{s.sub}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Prep toolkit */}
      <div className="mt-10">
        <h2 className="font-display text-2xl font-semibold">{t("dash.prepToolkit")}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          {[
            ...(isPremium ? [{ to: "/exams", icon: BookOpenCheck, title: "TCF Mocks", desc: "Real Reading & Listening tests", testid: "toolkit-exams" }] : []),
            { to: "/writing-lab", icon: PenLine, title: t("toolkit.writing"), desc: t("toolkit.writingDesc"), testid: "toolkit-writing" },
            { to: "/speaking-lab", icon: Mic, title: t("toolkit.speaking"), desc: t("toolkit.speakingDesc"), testid: "toolkit-speaking" },
            { to: "/samples", icon: BookOpenCheck, title: t("toolkit.samples"), desc: t("toolkit.samplesDesc"), testid: "toolkit-samples" },
            { to: "/templates", icon: Rocket, title: t("toolkit.templates"), desc: t("toolkit.templatesDesc"), testid: "toolkit-templates" },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div key={c.to} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Link to={c.to} data-testid={c.testid} className="group block rounded-2xl bg-card border border-border p-5 hover:shadow-lg hover:-translate-y-1 transition-all h-full">
                  <div className="h-10 w-10 rounded-xl emerald-surface grid place-items-center"><Icon className="h-5 w-5 text-white" /></div>
                  <h3 className="mt-3 font-semibold text-sm">{c.title}</h3>
                  <p className="text-xs text-muted-foreground">{c.desc}</p>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Coaches */}
      <div className="mt-10 flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold">{t("dash.yourCoaches")}</h2>
        <Link to="/coaches" className="text-sm font-semibold text-primary hover:underline">{t("dash.openAll")}</Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {agents.map((a, i) => {
          const Icon = AGENT_ICONS[a.key] || Mic;
          return (
            <motion.div key={a.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
              <Link to={`/coaches/${a.key}`} data-testid={`dash-agent-${a.key}`}
                className="group block rounded-2xl bg-card border border-border p-5 hover:shadow-lg hover:-translate-y-1 transition-all h-full">
                <div className="flex items-start justify-between">
                  <div className="h-11 w-11 rounded-xl emerald-surface grid place-items-center"><Icon className="h-5 w-5 text-white" /></div>
                  {a.voice && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-accent text-accent-foreground">VOICE</span>}
                </div>
                <h3 className="mt-3 font-semibold">{a.name}</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed line-clamp-3">{a.tagline}</p>
                <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary">{t("dash.start")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      {/* Topics feed + submit */}
      <div className="grid lg:grid-cols-3 gap-5 mt-10">
        <div className="lg:col-span-2 rounded-2xl bg-card border border-border p-6" data-testid="topics-feed">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /> {t("dash.latestTopics")}</h2>
          <p className="text-sm text-muted-foreground mb-4">{t("dash.latestTopicsSub")}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {["writing", "speaking"].map((tp) => (
              <div key={tp}>
                <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">{tp === "writing" ? "✍️ Expression écrite" : "🎙️ Expression orale"}</h3>
                <div className="space-y-2">
                  {(topics[tp] || []).length === 0 && <p className="text-sm text-muted-foreground italic">—</p>}
                  {(topics[tp] || []).map((topic, i) => (
                    <div key={i} className="rounded-xl bg-background border border-border p-3" data-testid={`topic-${tp}-${i}`}>
                      {topic.task && <span className="text-[10px] font-bold text-primary">{topic.task}</span>}
                      <p className="text-sm mt-0.5 leading-snug">{topic.content}</p>
                      {topic.exam_location && <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground"><MapPin className="h-3 w-3" /> {topic.exam_location}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
        <div className="rounded-2xl bg-card border border-border p-6" data-testid="submit-topic-card">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><PenSquare className="h-5 w-5 text-primary" /> {t("dash.justGave")}</h2>
          <p className="text-sm text-muted-foreground mb-3">{t("dash.justGaveSub")}</p>
          <div className="flex gap-2 mb-2">
            {["writing", "speaking"].map((tp) => (
              <button key={tp} onClick={() => setSubType(tp)} data-testid={`sub-type-${tp}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${subType === tp ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                {tp === "writing" ? "Writing" : "Speaking"}
              </button>
            ))}
          </div>
          <textarea data-testid="submit-topic-input" value={subText} onChange={(e) => setSubText(e.target.value)} rows={4}
            placeholder="e.g. Tâche 3: Should social media be regulated?" className="w-full p-3 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none" />
          <div className="mt-2 relative">
            <MapPin className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="submit-topic-location" value={subLoc} onChange={(e) => setSubLoc(e.target.value)}
              placeholder="Where did you take your exam? (e.g. Alliance Française, Toronto)"
              className="w-full pl-9 pr-3 h-10 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <button onClick={submitTopic} disabled={!subText.trim()} data-testid="submit-topic-btn"
            className="mt-2 w-full h-10 rounded-xl bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Send className="h-4 w-4" /> {t("dash.submitTopic")}
          </button>
        </div>

        {!isPremium && (
          <div className="rounded-2xl bg-card border border-border p-6" data-testid="dash-promo-card">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2"><Ticket className="h-5 w-5 text-primary" /> Have a promo code?</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-3">Got a code from a teacher or partner? Redeem it to unlock Premium instantly.</p>
            <div className="flex gap-2">
              <input value={redeemCode} onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="ENTER CODE" data-testid="dash-redeem-input"
                className="flex-1 h-10 px-3 rounded-xl bg-background border border-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring" />
              <button onClick={redeemPromo} disabled={redeeming || !redeemCode.trim()} data-testid="dash-redeem-btn"
                className="px-4 rounded-xl bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2">
                {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redeem"}
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
